import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import cx from 'classnames';

import { FormControl } from '@common/components/form-control';
import { Flex } from '@common/components/Flex';
import { Checkbox } from '@common/components/checkbox';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '../../database-security';
import { stringHasInteger } from '@common/utils/string';
import { ErrorMessage } from '@common/components/error-message';

import './user-security-settings.styles.scss';

const fields = {
  lockInactiveUsers: 'lockInactiveUsers',
  lockInactiveUsersDays: 'lockInactiveUsersDays',
  daysTillPasswordsExpire: 'daysTillPasswordsExpire',
  archivedPasswordsCount: 'archivedPasswordsCount',
  minPasswordsLength: 'minPasswordsLength',
};

export const UserSecuritySettings = (props) => {
  const { className, value, onChange, setMessageProps  } = props;

  const renderSetting = React.useCallback((children) => {
    return (
      <Flex alignItems="center" className="UserSecuritySettings__setting">
        {children}
      </Flex>
    );
  }, []);

  const handleInputChange = React.useCallback((e) => {
    e.persist();
    let { value: inputValue } = e.target;

    if (inputValue.length > 1 && inputValue.startsWith('0')) {
      inputValue = inputValue.substring(1)
    }

    const validations = [
      {
        isValid: () => stringHasInteger(inputValue, { allowZero: true, allowEmpty: true, allowNegative: false }),
      },
    ];

    const isValid = validations.every(({ isValid }) => isValid());

    if (isValid) {
      onChange({
        ...value,
        [e.target.dataset.field]: inputValue ? Number(inputValue) : 0,
      });
    }
  }, [value]);

  const handleInputBlur = React.useCallback((e) => {
    e.persist();
    let { value: inputValue } = e.target;
    const passwordMinLength = 8;

    const validations = [
      {
        shouldValidate: e.target.dataset.field === fields.minPasswordsLength,
        isValid: () => Number(inputValue) >= passwordMinLength,
        defaultValidValue: passwordMinLength,
        messageProps: {
          header: I18n.t('Operation Canceled'),
          content: <ErrorMessage>  {I18n.t('The minimum password length must be at least 8', { amount: passwordMinLength })}</ErrorMessage>,
        }
      }
    ];

    const notPassedValidations = validations.filter(({ shouldValidate = true, isValid }) => shouldValidate ? !isValid() : true);
    
    if (notPassedValidations.length) {
      inputValue = notPassedValidations[0].defaultValidValue;

      setMessageProps({
        ...notPassedValidations[0]?.messageProps,
        show: true,
        confirmText: 'Ok',
        onCustomOk: () => setMessageProps({ show: false }),
      });
    }

    onChange({
      ...value,
      [e.target.dataset.field]: Number(inputValue),
    });
  }, [value]);

  const handleCheckboxChange = React.useCallback((_, { e }) => {
    e.persist();
    onChange({
      ...value,
      [e.target.dataset.field]: e.target.checked,
    });
  }, [value]);

  return (
    <section className={cx('UserSecuritySettings', className)}>
      <h5 className="UserSecuritySettings__title">{I18n.t('User Security Settings')}</h5>
      {renderSetting(
        <>
          <Checkbox 
            checked={value.lockInactiveUsers} 
            data-field={fields.lockInactiveUsers}
            className="UserSecuritySettings__checkbox"
            {...getTestAttributes(MODULE_NAME, 'user-security-settings-lockInactiveUsers', 'checkbox')}
            onChange={handleCheckboxChange} 
          >
            {I18n.t('Lock Inactive Users After:')} 
          </Checkbox>
          <FormControl 
            onChange={handleInputChange} 
            data-field={fields.lockInactiveUsersDays}
            value={value.lockInactiveUsersDays} 
            {...getTestAttributes(MODULE_NAME, 'user-security-settings-lockInactiveUsersDays', 'input')}
            className="UserSecuritySettings__input" 
          />
          {I18n.t('Days')}
        </>
      )}
      {renderSetting(
        <>
          {I18n.t('Password Expire After:')} 
          <FormControl
            onChange={handleInputChange} 
            data-field={fields.daysTillPasswordsExpire}
            value={value.daysTillPasswordsExpire} 
            {...getTestAttributes(MODULE_NAME, 'user-security-settings-daysTillPasswordsExpire', 'input')}
            className="UserSecuritySettings__input" 
          /> 
          {I18n.t('Days')}
        </>
      )}
      {renderSetting(
        <>
          {I18n.t('Archive the previous:')} 
          <FormControl 
            onChange={handleInputChange} 
            data-field={fields.archivedPasswordsCount}
            value={value.archivedPasswordsCount} 
            {...getTestAttributes(MODULE_NAME, 'user-security-settings-archivedPasswordsCount', 'input')}
            className="UserSecuritySettings__input" 
          /> 
          {I18n.t('Passwords')}
        </>
      )}
      {renderSetting(
        <>
          {I18n.t('Minimum password length:')} 
          <FormControl 
            onChange={handleInputChange} 
            onBlur={handleInputBlur}
            data-field={fields.minPasswordsLength}
            value={value.minPasswordsLength} 
            {...getTestAttributes(MODULE_NAME, 'user-security-settings-minPasswordsLength', 'input')}
            className="UserSecuritySettings__input" 
          /> 
          {I18n.t('Characters')}
        </>
      )}
    </section>
  );
};